import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Row, Col, Modal } from 'react-bootstrap';
import get from 'lodash/get';
import useQuery from '../../../../../../../modules/react/hooks/useQuery';
import { formatMoveInDate } from '../../../../../../../modules/datetime';
import { FacilityInfo, Price } from '../../../../../../../src/web/components';
import Address from '../../../../../stateless/Address';
import Button from '../../../../../../../modules/react/components/stateless/Button';
import Phone from '../../../../../stateless/Phone';
import { TRACK_SUBMIT_RESERVATION } from '../../../../../../../src/web/utils/dataLayer';
import styles from '../Actions.scss';
import UnitDescription from './UnitDescription';
import AppliedPromos from './AppliedPromos';
import useCurrentPage from '../../../../../../hooks/useCurrentPage';
import FetchingData from '../../../../FetchingData/FetchingData';
import useEventTrigger from '../../../../../../../modules/react/hooks/useEventTrigger';

const GET_REVIEW_COST = gql`
  query reviewCost($receipt: ReceiptInput!) {
    getReviewCostResponse(receipt: $receipt) {
      dueAtMoveIn
      moveInTaxesTotal
      moveInSubtotal
    }
  }
`;

const ReservationConfirmation = (props) => {
  const {
    facility,
    price,
    discountedPrice,
    discountPlans,
    receipt,
    onClose,
    enableMoveInNow,
  } = props;

  const {
    primaryColorClass,
    secondaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    secondaryColorBorderClass,
    primaryColorBorderHoverClass,
    secondaryColorClass,
    primaryColorHoverClass,
    buttonStyleClass,
  } = useCurrentPage();

  const { id, tenant, moveInDate, dueAtMoveIn, invoiceableFees = [] } = receipt || {};
  const { addEvent } = useEventTrigger();

  const receiptPayload = {
    id: receipt.id,
    facilityId: facility.id,
    tenant: {
      address1: receipt?.tenant?.address1 || null,
      city: receipt?.tenant?.city || null,
      state: receipt?.tenant?.state || null,
      postal: receipt?.tenant?.postal || null,
    },
  };
  const { data, loading, error } = useQuery(GET_REVIEW_COST, {
    variables: { receipt: receiptPayload },
  });


  useEffect(() => {
    if (receiptPayload.id) {
      const payload = {
        id: receiptPayload.id,
        tenantCity: receiptPayload.tenant.city,
        tenantState: receiptPayload.tenant.city,
        facilityId: receiptPayload.facilityId,
      };
      addEvent(TRACK_SUBMIT_RESERVATION, payload, 'submit');
    }
  }, [receiptPayload.id]);

  const renderMoveInFeesBreakdown = () => {
    if (loading && !error) {
      return (
        <FetchingData name={'Due At Move-In '} />
      );
    } if (data?.getReviewCostResponse) {
      const { dueAtMoveIn: dueAtMoveInFromQuery,
        moveInTaxesTotal, moveInSubtotal } = data?.getReviewCostResponse || {};

      return (
        <>
          {moveInSubtotal > 0 && (
            <>
              <Col xs={6}>
                Move-In Subtotal
              </Col>
              <Col xs={6} className="text-end">
                <Price value={moveInSubtotal} />
              </Col>
            </>
          )}
          {moveInTaxesTotal > 0 && (
            <>
              <Col xs={6}>
                Taxes
              </Col>
              <Col xs={6} className="text-end">
                <Price value={moveInTaxesTotal} />
              </Col>
            </>
          )}
          <Col xs={6} className={styles.lastAmountRow}>
            Move-In Total*
          </Col>
          <Col xs={6} className={`text-end ${styles.lastAmountRow}`}>
            <Price value={dueAtMoveInFromQuery} />
          </Col>
        </>
      );
    }

    return (
      <>
        <Col xs={6} className={styles.lastAmountRow}>
          Move-In Total*
        </Col>
        <Col xs={6} className={`text-end ${styles.lastAmountRow}`}>
          <Price value={dueAtMoveIn} />
        </Col>
      </>
    );
  };

  const renderConfirmationText = () => get(facility, 'settings.reservationConfirmation');

  const renderPrice = (priceValue, className) => {
    const { hidePricingDisplayCall } = facility.settings;
    if (hidePricingDisplayCall) {
      return <div>CALL</div>;
    }
    return (
      <Price
        element="div"
        className={`${className} ${styles.price}`}
        value={priceValue}
        decimals={2}
      />
    );
  };

  const renderRates = () => (
    <div>
      <Row>
        <Col xs={6}>
          {discountedPrice ? 'Web Rate' : 'Standard Rate'}
        </Col>
        <Col xs={6}>
          {discountedPrice
            ? renderPrice(price, styles.linethrough)
            : renderPrice(price, 'text-end')}
        </Col>
      </Row>
      {discountedPrice && (
        <Row>
          <Col xs={6} className={styles.discountedRate}>
            Discounted Rate
          </Col>
          <Col xs={6}>
            {renderPrice(discountedPrice, `text-end ${styles.discountedRate}`)}
          </Col>
        </Row>
      )}
      <Col xs={9} className={styles.appliedPromos}>
        <AppliedPromos
          discountPlans={receipt?.discountPlans || discountPlans}
          enablePublicDescription={facility?.settings?.enablePublicDescription}
          reservationConfirmation
        />
      </Col>
    </div>
  );

  const renderFees = () => (
    invoiceableFees.map(fee => (
      <React.Fragment key={`fee-${fee?.id || fee?.description}`}>
        <Col xs={6}>
          {fee?.description}
        </Col>
        <Col xs={6} className="text-end">
          <Price value={fee?.amount} />
        </Col>
      </React.Fragment>
    ))
  );

  return (
    <div {...TRACK_SUBMIT_RESERVATION}>
      <Modal.Header closeButton>
        <div className={`${styles.reserveTitle} ${primaryColorClass}`}>
          Reservation Confirmation
        </div>
      </Modal.Header>
      <Modal.Body>
        <FacilityInfo facility={facility} />
        <div className={styles.tenantDetails}>
          <div>
            {tenant?.firstName} {tenant?.lastName}
          </div>
          {tenant?.address1 && <Address {...tenant} />}
          <Phone number={tenant.phone} />
          <div>{tenant?.email}</div>
        </div>
        <div className={styles.blurb}>
          {renderConfirmationText()}
        </div>
        {moveInDate && (
          <>
            <div className={styles.subHeading}>
              Move-In Date
            </div>
            <Row className={styles.descriptionWell}>
              <Col xs={12}>
                {formatMoveInDate(moveInDate)}
              </Col>
            </Row>
          </>
        )}
        <div className={styles.subHeading}>
          Size / Features
        </div>
        <Row className={styles.descriptionWell}>
          <Col xs={12}>
            <UnitDescription
              {...props}
              styles={styles}
            />
          </Col>
        </Row>
        <div className={styles.subHeading}>
          Amount Due At Move-In
        </div>
        <Row className={styles.descriptionWell}>
          <div className={styles.monthlyRate}>
            <Row>
              {renderRates()}
            </Row>
            <Row>
              <Col xs={6}>
                Reservation Fee
              </Col>
              <Col xs={6} className="text-end">
                <Price value={0} />
              </Col>
              {renderFees()}
              {renderMoveInFeesBreakdown()}
            </Row>
          </div>
        </Row>
        <p className={styles.disclaimer}>
          *May be subject to local taxes.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          onClick={onClose}
          variant="primary"
          className={`${secondaryColorBackgroundClass} ${primaryColorBackgroundHoverClass} ${buttonStyleClass}`}
        >
          Close
        </Button>
        {enableMoveInNow && (
          <Button
            type="submit"
            variant="primary"
            className={`
              ${secondaryColorBorderClass}
              ${primaryColorBorderHoverClass}
              ${secondaryColorClass}
              ${primaryColorHoverClass}
            `}
            style={{ backgroundColor: '#fff', border: '1px solid' }}
            id="movein"
            href={`move-in/${id}`}
            data-testid="actions-movein"
          >
            Move in Now
          </Button>
        )}
      </Modal.Footer>
    </div>
  );
};

ReservationConfirmation.propTypes = {
  facility: PropTypes.shape({
    settings: PropTypes.shape({
      reservationConfirmation: PropTypes.string,
      hidePricingDisplayCall: PropTypes.bool,
      enablePublicDescription: PropTypes.bool,
    }),
  }).isRequired,
  price: PropTypes.number.isRequired,
  discountedPrice: PropTypes.number,
  discountPlans: PropTypes.arrayOf(PropTypes.object),
  isSubmitting: PropTypes.bool.isRequired,
  receipt: PropTypes.shape({
    id: PropTypes.string,
    tenant: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
    moveInDate: PropTypes.string.isRequired,
    dueAtMoveIn: PropTypes.number.isRequired,
    invoiceableFees: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        description: PropTypes.string,
      }),
    ),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  enableMoveInNow: PropTypes.bool.isRequired,
};

ReservationConfirmation.defaultProps = {
  discountedPrice: null,
  discountPlans: [],
};


ReservationConfirmation.fragments = {
  facility: gql`
    fragment ReservationConfirmationFacility on Facility {
      settings {
        enablePublicDescription
        hidePricingDisplayCall
        reservationConfirmation
      }
      ...FacilityInfo
    }
    ${FacilityInfo.fragments.facility}
  `,
  // We omit moveInDate for the RentalShortForm since we hardcode it to the
  // current date.
  reservation: gql`
    fragment ReservationConfirmationReservation on Reservation {
      charges {
        amount
        description
      }
      tenant {
        email
        firstName
        lastName
        phoneNumber
      }
      totalDue
    }
  `,
  unitGroup: gql`
    fragment ReservationConfirmationUnitGroup on UnitGroup {
      discountedPrice
      discountPlans {
        ...AppliedPromosDiscountPlan
      }
      price
      ...UnitDescriptionUnitGroup
    }
    ${AppliedPromos.fragments.discountPlan}
    ${UnitDescription.fragments.unitGroup}
  `,
};

export default ReservationConfirmation;
